import { Key, degreeToken } from './key';
import { Chord } from './roman';
import { mod, pcNumber } from './pitch';

/**
 * Deterministic recognition of well-worn paths (cadences, schemata, sequences)
 * in a sequence of chords. A pattern is a list of stages; each stage may constrain
 * the Roman numeral, the bass scale degree, or both.
 */

export interface StageSpec {
  /** Accepted Roman numerals (any of). */
  rn?: string[];
  /** Accepted bass degrees, e.g. ["4"], ["#4"], ["7", "5"]. */
  bass?: string[];
}

export interface PatternSpec {
  id: string;
  stages: StageSpec[];
}

export interface ChordToken {
  symbol: string;
  chord: Chord;
  key: Key;
}

export interface PathMatch {
  id: string;
  start: number;
  /** Index of the last chord (inclusive). */
  end: number;
}

export function normalizeRn(s: string): string {
  return s.replace(/°/g, 'o').replace(/♭/g, 'b').replace(/♯/g, '#').replace(/^([^/]*?)53$/, '$1').replace(/^([^/]*?)63$/, '$16');
}

function stageMatches(stage: StageSpec, token: ChordToken): boolean {
  if (stage.rn && !stage.rn.map(normalizeRn).includes(normalizeRn(token.symbol))) return false;
  if (stage.bass && !stage.bass.includes(degreeToken(token.chord.bass, token.key))) return false;
  return true;
}

/** Every occurrence of every pattern. */
export function matchPatterns(tokens: ChordToken[], specs: PatternSpec[]): PathMatch[] {
  const out: PathMatch[] = [];
  for (const spec of specs) {
    const n = spec.stages.length;
    for (let i = 0; i + n <= tokens.length; i++) {
      let ok = true;
      for (let k = 0; k < n && ok; k++) ok = stageMatches(spec.stages[k], tokens[i + k]);
      if (ok) out.push({ id: spec.id, start: i, end: i + n - 1 });
    }
  }
  return out;
}

/** Runs of roots falling by fifth (or rising by fourth): the "falling dominoes" sequence. */
export function findFifthsSequences(tokens: ChordToken[], minChords = 4): PathMatch[] {
  const out: PathMatch[] = [];
  let runStart = 0;
  for (let i = 1; i <= tokens.length; i++) {
    let falling = false;
    if (i < tokens.length) {
      const a = tokens[i - 1].chord.root;
      const b = tokens[i].chord.root;
      const semis = mod(pcNumber(b) - pcNumber(a), 12);
      falling = mod(b.letter - a.letter, 7) === 3 && (semis === 5 || semis === 6);
    }
    if (!falling) {
      if (i - runStart >= minChords) out.push({ id: 'fifths-sequence', start: runStart, end: i - 1 });
      runStart = i;
    }
  }
  return out;
}

/** The most specific matches that end on the last chord (what the Walk instrument reports). */
export function matchesEndingNow(tokens: ChordToken[], specs: PatternSpec[]): PathMatch[] {
  const last = tokens.length - 1;
  const all = [...matchPatterns(tokens, specs), ...findFifthsSequences(tokens)].filter((m) => m.end === last);
  return all.sort((a, b) => b.end - b.start - (a.end - a.start));
}
